import {
  ArrowDown01Icon,
  CancelCircleIcon,
  Search01Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react-native';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';
import { Menu, TextInput as PaperTextInput } from 'react-native-paper';
import { Colors } from '../constants/Colors';
import { menuContentStyle } from '../constants/Styles';
import { useDynamicStyles } from '../hooks/useDynamicStyles';
import { isIos } from '../utils/responsivity';
import { Button } from './Button';
import { Loader } from './Loader';
import { TextReg } from './StyledText';
import { TextInput } from './TextInput';

export type SearchAndSelectOption = {
  label: string;
  value: string;
};

type Props = {
  placeholder: string;
  selectedValue?: string;
  onSelect: (value: string | undefined) => void;
  options: SearchAndSelectOption[];
  isLoading?: boolean;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  loadingText?: string;
  noResultsText?: string;
  noResultsContent?:
    | React.ReactNode
    | ((closeMenu: () => void) => React.ReactNode);
  buttonStyle?: StyleProp<ViewStyle>;
  menuStyle?: StyleProp<ViewStyle>;
  // When true, allow opening the menu even when options are empty
  allowOpenWhenEmpty?: boolean;
  // External signal to programmatically open the menu (increment to trigger)
  openMenuSignal?: number;
  // When provided, the anchor renders this icon centered instead of text
  anchorIcon?: typeof Search01Icon;
  // Center the anchor content (useful when using anchorIcon)
  anchorCentered?: boolean;
};

export function SearchAndSelectInput({
  placeholder,
  selectedValue,
  onSelect,
  options,
  isLoading = false,
  searchQuery: externalSearchQuery,
  onSearchChange: externalOnSearchChange,
  noResultsText = 'No results found',
  noResultsContent,
  buttonStyle,
  menuStyle,
  allowOpenWhenEmpty = false,
  openMenuSignal,
  anchorIcon,
  anchorCentered = false,
}: Props) {
  const styles = useDynamicStyles(createStyles);
  const [internalSearchQuery, setInternalSearchQuery] = useState('');
  const [isMenuVisible, setIsMenuVisible] = useState(false);

  // Use external search state if provided, otherwise use internal state
  const searchQuery = externalSearchQuery ?? internalSearchQuery;
  const onSearchChange = externalOnSearchChange ?? setInternalSearchQuery;

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) {
      return options;
    }
    const searchLower = searchQuery.toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchLower),
    );
  }, [options, searchQuery]);

  // Get selected option
  const selectedOption = useMemo(() => {
    return options.find((option) => option.value === selectedValue);
  }, [options, selectedValue]);

  const handleClear = useCallback(() => {
    onSelect(undefined);
    onSearchChange('');
  }, [onSelect, onSearchChange]);

  const handleSelect = useCallback(
    (value: string) => {
      onSelect(value);
      onSearchChange('');
      setIsMenuVisible(false);
    },
    [onSelect, onSearchChange],
  );

  const openMenu = () => {
    if (options.length || allowOpenWhenEmpty) {
      setIsMenuVisible(true);
    }
  };

  const closeMenu = () => {
    setIsMenuVisible(false);
  };

  const handleIconPress = () => {
    if (selectedOption) {
      handleClear();
    }
  };

  const canClearSelection = selectedOption;
  const selectedLabel = selectedOption?.label;

  // Open menu when external signal changes
  useEffect(() => {
    if (typeof openMenuSignal === 'number') {
      setIsMenuVisible(true);
    }
  }, [openMenuSignal]);

  return (
    <View style={menuStyle}>
      <Menu
        visible={isMenuVisible}
        onDismiss={closeMenu}
        contentStyle={menuContentStyle}
        anchor={
          <Button
            mode="contained"
            onPress={openMenu}
            style={[buttonStyle]}
            labelStyle={styles.buttonLabel}
            buttonColor={isIos ? Colors.opacity025 : Colors.opacity05}
            size="medium"
            disabled={options.length === 0 && isLoading}
          >
            {/* Hidden content to preserve button size; if icon anchor, use a space */}
            <TextReg style={styles.hiddenText}>
              {anchorIcon ? ' ' : (selectedLabel ?? placeholder)}
            </TextReg>
          </Button>
        }
        anchorPosition="bottom"
      >
        {/* Search input in menu */}
        <View style={styles.searchContainer}>
          <TextInput
            showLabel={false}
            placeholder="Search..."
            value={searchQuery}
            onChange={onSearchChange}
            autoCorrect={false}
            style={styles.searchInput}
            left={
              <PaperTextInput.Icon
                icon={() => (
                  <HugeiconsIcon
                    icon={Search01Icon}
                    color={Colors.text}
                    size={20}
                  />
                )}
                loading={isLoading}
              />
            }
          />
        </View>

        {/* Results */}
        {isLoading ? (
          <Menu.Item
            title="Loading..."
            disabled
            titleStyle={{ color: Colors.placeholderText, fontStyle: 'italic' }}
          />
        ) : filteredOptions.length > 0 ? (
          filteredOptions.map((option) => (
            <Menu.Item
              onPress={() => handleSelect(option.value)}
              title={option.label}
              key={option.value}
              titleStyle={{ color: Colors.text }}
            />
          ))
        ) : noResultsContent ? (
          <View style={styles.noResultsContainer}>
            {typeof noResultsContent === 'function'
              ? noResultsContent(closeMenu)
              : noResultsContent}
          </View>
        ) : (
          <Menu.Item
            title={noResultsText}
            disabled
            titleStyle={{ color: Colors.placeholderText, fontStyle: 'italic' }}
          />
        )}
      </Menu>

      {/* Absolute overlay content - matches Dropdown pattern */}
      <View
        style={[
          styles.absoluteContent,
          anchorCentered && { justifyContent: 'center' },
        ]}
      >
        {anchorIcon ? (
          <HugeiconsIcon icon={anchorIcon} color={Colors.text} />
        ) : selectedLabel ? (
          <TextReg numberOfLines={1} style={styles.text}>
            {selectedLabel}
          </TextReg>
        ) : (
          <TextReg numberOfLines={1} style={styles.placeholder}>
            {placeholder}
          </TextReg>
        )}
        {!anchorIcon && (
          <>
            {options.length ? (
              canClearSelection ? (
                <View style={styles.iconContainer} onTouchEnd={handleIconPress}>
                  <HugeiconsIcon icon={CancelCircleIcon} color={Colors.text} />
                </View>
              ) : (
                <HugeiconsIcon icon={ArrowDown01Icon} color={Colors.text} />
              )
            ) : (
              isLoading && <Loader style={{ alignItems: 'flex-end' }} />
            )}
          </>
        )}
      </View>
    </View>
  );
}

function createStyles() {
  return StyleSheet.create({
    buttonLabel: {
      paddingRight: 16,
    },
    absoluteContent: {
      position: 'absolute',
      backgroundColor: 'transparent',
      pointerEvents: 'none',
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingLeft: 16,
      paddingRight: 16,
      height: 44,
      width: '100%',
      gap: 10,
    },
    content: {
      height: 44,
    },
    text: {
      fontSize: 16,
    },
    placeholder: {
      fontSize: 16,
      color: Colors.placeholderText,
    },
    hiddenText: {
      fontSize: 16,
      color: 'transparent',
    },
    iconContainer: {
      padding: 8,
      margin: -8,
    },
    searchContainer: {
      padding: 12,
      paddingBottom: 8,
    },
    searchInput: {
      backgroundColor: Colors.opacity10,
    },
    noResultsContainer: {
      padding: 12,
    },
  });
}
